import { eq, gt, isNotNull, sql } from "drizzle-orm";

import { createLogger } from "@/logger";

const log = createLogger("reset");

import { db } from "@/db/db.ts";
import { feed, feedPreference, item } from "@/db/schema.ts";
import { type DeletionResult, deleteItemsAndOrphanedContent } from "@/services/LocalContentDeletion";

const FEED_ITEM_PAGE_SIZE = 50;
const WARM_WINDOW_MS = 60 * 60 * 1000; // 1 hour
const COOL_WINDOW_MS = 24 * 60 * 60 * 1000; // 1 day

export type EvictionResult = DeletionResult;

export async function applyFeedEviction(): Promise<EvictionResult> {
  const now = Date.now();
  const warmCutoff = new Date(now - WARM_WINDOW_MS).toISOString();
  const coolCutoff = new Date(now - COOL_WINDOW_MS).toISOString();

  const feedCapsCte = db.$with("feed_caps").as(
    db
      .select({
        feedId: feed.id,
        cap: sql<number | null>`CASE
          WHEN ${feed.lastOpened} IS NOT NULL AND ${feed.lastOpened} >= ${warmCutoff}::timestamptz THEN NULL
          WHEN ${feed.lastOpened} IS NOT NULL AND ${feed.lastOpened} >= ${coolCutoff}::timestamptz THEN ${FEED_ITEM_PAGE_SIZE}
          WHEN COALESCE(${feedPreference.pushNotificationPreference}, 'none') != 'none' THEN ${FEED_ITEM_PAGE_SIZE}
          ELSE 1
        END`.as("cap"),
      })
      .from(feed)
      .leftJoin(feedPreference, eq(feedPreference.feedId, feed.id)),
  );

  const rankedItemsCte = db.$with("ranked_items").as(
    db
      .select({
        id: item.id,
        feedId: item.feedId,
        cap: feedCapsCte.cap,
        rn: sql<number>`ROW_NUMBER() OVER (PARTITION BY ${item.feedId} ORDER BY ${item.createdAt} DESC)`.as("rn"),
      })
      .from(item)
      .innerJoin(feedCapsCte, eq(feedCapsCte.feedId, item.feedId))
      .where(isNotNull(feedCapsCte.cap)),
  );

  const itemsToEvict = await db
    .with(feedCapsCte, rankedItemsCte)
    .select({
      id: rankedItemsCte.id,
      feedId: rankedItemsCte.feedId,
    })
    .from(rankedItemsCte)
    .where(gt(rankedItemsCte.rn, rankedItemsCte.cap));

  if (itemsToEvict.length === 0) {
    log.debug("[applyFeedEviction] No items to evict");
    return { deletedItems: 0, deletedContentIds: 0, affectedFeeds: 0 };
  }

  const itemIds = itemsToEvict.map((r) => r.id);
  const feedIds = [...new Set(itemsToEvict.map((r) => r.feedId))];

  return deleteItemsAndOrphanedContent(itemIds, feedIds, "applyFeedEviction");
}
